import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Map, Search, Lightbulb, ChevronDown } from 'lucide-react';
import CollegeSearchTab from './CollegeSearchTab';
import SuggestedCollegesTab from './SuggestedCollegesTab';
import CollegeMapView from './CollegeMapView';
import CollegeSearchSidebar from './CollegeSearchSidebar';
import { CollegeFilters } from '@/hooks/useCollegeSearch';
import { useAuth } from '@/contexts/AuthContext';
import { toggleSaveCollege } from '@/hooks/useCollegeSearch';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { Maximize2 } from 'lucide-react';
import { useMembershipAccess } from '@/hooks/useMembershipAccess';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

interface CollegeSearchModuleProps {
  initialTab?: string;
}

const CollegeSearchModule: React.FC<CollegeSearchModuleProps> = ({ 
  initialTab = "search" 
}) => {
  const { profile } = useAuth();
  const { hasAccessTo } = useMembershipAccess();
  const navigate = useNavigate();
  const isPremium = hasAccessTo('college-search');
  const [mapFilters, setMapFilters] = useState<CollegeFilters>({
    states: [],
    divisions: [],
    conferences: [],
    religiousAffiliations: [],
    tuitionMin: null,
    tuitionMax: null,
    satMin: null,
    satMax: null,
    actMin: null,
    actMax: null,
    gpaMin: null,
    gpaMax: null,
    sports: [],
    searchQuery: ''
  });

  // Filter options for the map view
  const stateOptions = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 
    'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];
  const divisionOptions = ['NCAA D1', 'NCAA D2', 'NCAA D3', 'NAIA', 'NJCAA', 'NCCAA', 'CCCAA', 'Other'];

  const toggleMapState = (state: string) => {
    setMapFilters(prev => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state]
    }));
  };

  const toggleMapDivision = (division: string) => {
    setMapFilters(prev => ({
      ...prev,
      divisions: prev.divisions.includes(division)
        ? prev.divisions.filter(d => d !== division)
        : [...prev.divisions, division]
    }));
  };

  const clearMapFilters = () => {
    setMapFilters(prev => ({
      ...prev,
      states: [],
      divisions: []
    }));
  };

  const handleToggleSave = async (collegeId: string) => {
    if (!profile?.id) {
      toast.error("Please log in to save colleges");
      navigate('/auth');
      return;
    }
    
    if (!isPremium) {
      toast.error("Saving colleges is a premium feature. Upgrade to save colleges!", {
        action: {
          label: "Upgrade",
          onClick: () => navigate('/athlete-dashboard/membership')
        }
      });
      return;
    }
    
    const result = await toggleSaveCollege(collegeId, profile.id);
    if (result.success) {
      toast.success(result.action === 'added' ? 'College saved!' : 'College removed from saved');
    } else {
      toast.error('Failed to save college');
    }
    return result;
  };

  return (
    <div className="space-y-4">
      {/* Header Section with Tabs */}
      <Tabs defaultValue={initialTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="search" className="flex items-center gap-2 font-heading font-bold uppercase text-sm">
              <Search className="h-4 w-4" />
              Search
            </TabsTrigger>
            <TabsTrigger value="map" className="flex items-center gap-2 font-heading font-bold uppercase text-sm">
              <Map className="h-4 w-4" />
              Map View
            </TabsTrigger>
            <TabsTrigger value="suggested" className="flex items-center gap-2 font-heading font-bold uppercase text-sm">
              <Lightbulb className="h-4 w-4" />
              Suggested
            </TabsTrigger>
          </TabsList>
          
          <Link to="/college-map">
            <Button variant="outline" size="sm" className="flex items-center gap-2 font-heading font-bold uppercase">
              <Maximize2 className="h-4 w-4" />
              Full Screen Map
            </Button>
          </Link>
        </div>
        
        <TabsContent value="search">
          <div className="flex gap-6">
            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              <CollegeSearchTab />
            </div>

            {/* Right Sidebar - Hidden on mobile, aligned with cards below filters */}
            <aside className="hidden lg:block w-72 shrink-0 mt-48">
              <CollegeSearchSidebar />
            </aside>
          </div>
        </TabsContent>
        
        <TabsContent value="map">
          <div className="space-y-4">
            {/* Inline Filters for Map View */}
            <div className="flex flex-wrap items-center gap-3">
              {/* States Dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 font-heading font-bold uppercase text-sm">
                    States
                    {mapFilters.states.length > 0 && (
                      <Badge variant="secondary" className="ml-1">{mapFilters.states.length}</Badge>
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2 max-h-72 overflow-y-auto">
                  <div className="grid grid-cols-1 gap-1">
                    {stateOptions.map((state) => (
                      <label 
                        key={state} 
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={mapFilters.states.includes(state)}
                          onCheckedChange={() => toggleMapState(state)}
                        />
                        {state}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Division Dropdown */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2 font-heading font-bold uppercase text-sm">
                    Division
                    {mapFilters.divisions.length > 0 && (
                      <Badge variant="secondary" className="ml-1">{mapFilters.divisions.length}</Badge>
                    )}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2">
                  <div className="grid grid-cols-1 gap-1">
                    {divisionOptions.map((division) => (
                      <label 
                        key={division} 
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={mapFilters.divisions.includes(division)}
                          onCheckedChange={() => toggleMapDivision(division)}
                        />
                        {division}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Clear Filters */}
              {(mapFilters.states.length > 0 || mapFilters.divisions.length > 0) && (
                <Button variant="ghost" size="sm" onClick={clearMapFilters} className="font-heading font-bold uppercase text-sm">
                  Clear Filters
                </Button>
              )}
            </div>
            
            <CollegeMapView 
              filters={mapFilters} 
              onToggleSave={handleToggleSave}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="suggested">
          <SuggestedCollegesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CollegeSearchModule;
